import React, { useEffect, useState } from "react";
import { Container, Col } from "react-bootstrap";
import {
  VALID_TENANT_URLS,
  TENANT_URL_TO_FRONTEND_URL,
  fetchWithoutAuth,
} from "../../../../api-client/APIClient";
import { Spinner } from "@merge-api/merge-javascript-shared";
import * as Sentry from "@sentry/react";
import { MERGE_THIRD_PARTY_COOKIES_HELP_CENTER } from "../../../../router/RouterUtils";

const QBOBankFeedCallbackScreen = () => {
  const params = new URLSearchParams(window.location.search);
  const end_user_origin_id = params.get("end_user_origin_id") ?? "";
  const state = params.get("state") ?? "";

  let tenant_url = "";
  let client_id = "";
  let redirect_url = "https://merge.dev";
  let original_state = "";
  let data_provider_login_uri = "";
  if (state !== "") {
    try {
      const parsedState = JSON.parse(state);
      tenant_url = parsedState["tenant_url"] ?? "";
      client_id = parsedState["client_id"] ?? "";
      redirect_url = parsedState["redirect_url"] ?? redirect_url;
      original_state = parsedState["original_state"] ?? "";
      data_provider_login_uri = parsedState["data_provider_login_uri"] ?? "";
    } catch {
      // If parsing fails, values remain as defaults
    }
  }

  let isMergeTenantURL = false;
  if (tenant_url !== "") {
    isMergeTenantURL = VALID_TENANT_URLS.includes(tenant_url);
  } else {
    isMergeTenantURL = true;
  }

  const [isProcessing, setIsProcessing] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(
    "Error processing authorization. Please close window to try again.",
  );

  const hasRedirectError = params.has("error") || !isMergeTenantURL || !end_user_origin_id;

  const postProcessContent =
    hasRedirectError || hasError
      ? errorMessage
      : `Authorization successful! Redirecting to QuickBooks Online...`;

  useEffect(() => {
    const processAuthorization = async () => {
      if (!end_user_origin_id) {
        setErrorMessage("Missing end_user_origin_id parameter.");
        setHasError(true);
        setIsProcessing(false);
        return;
      }

      if (isMergeTenantURL) {
        if (tenant_url in TENANT_URL_TO_FRONTEND_URL) {
          const frontend_url = TENANT_URL_TO_FRONTEND_URL[tenant_url];
          if (frontend_url && frontend_url !== "" && !window.location.href.includes(frontend_url)) {
            // Use window.location.replace to properly redirect
            window.location.replace(
              window.location.href.replace(window.location.origin, frontend_url),
            );
            return; // Exit early since we're redirecting
          }
        }

        fetchWithoutAuth({
          path: "/integrations/intuit/oauth-authorize",
          method: "POST",
          body: {
            end_user_origin_id,
            redirect_url,
            client_id,
            data_provider_login_uri,
          },
          onResponse: (data) => {
            setIsProcessing(false);
            const authorization_code = data.authorization_code;
            const qboRedirectUrl = `${redirect_url}?authorization_code=${authorization_code}&state=${encodeURIComponent(
              original_state,
            )}`;
            window.location.href = qboRedirectUrl;
          },
          onError: async (response: Response | undefined) => {
            const responseJson = await response?.json();
            const nonFieldErrors = responseJson?.non_field_errors;
            let capturedSpecificError = false;
            if (nonFieldErrors?.length > 0) {
              const errorMessage = nonFieldErrors[0];
              Sentry.captureMessage(errorMessage);
              capturedSpecificError = true;
              if (errorMessage === "CSRF token mismatch") {
                setErrorMessage(
                  <>
                    CSRF verification failed. Please make sure{" "}
                    <a href={MERGE_THIRD_PARTY_COOKIES_HELP_CENTER}>
                      third party cookies are enabled in your browser
                    </a>{" "}
                    and retry.
                  </>,
                );
              }
            }
            if (!capturedSpecificError) {
              Sentry.captureMessage("QBO Bank Feed authorization failed.");
            }
            setHasError(true);
            setIsProcessing(false);
          },
        });
      }
    };

    processAuthorization();
  }, [
    end_user_origin_id,
    isMergeTenantURL,
    tenant_url,
    client_id,
    redirect_url,
    original_state,
    data_provider_login_uri,
  ]);

  return (
    <Container fluid className="h-screen d-flex align-items-center justify-content-center">
      <Col className="d-flex flex-grow-1 align-items-center justify-content-center">
        {isProcessing && !hasRedirectError && !hasError ? (
          <Spinner />
        ) : (
          <span>{postProcessContent}</span>
        )}
      </Col>
    </Container>
  );
};

export default QBOBankFeedCallbackScreen;
